/**
 * Feature flag utility functions
 *
 * Environment Variable: REACT_APP_PIXEL_4_HOUR_CADENCE_ENABLED
 *
 * When set to 'false':
 * - Hides the "Every 4 hours" option from all pixel cadence selectors
 * - Forces all pixel cadence values to "Every 24 hours"
 * - Prevents users from selecting 4-hour cadence
 *
 * When set to 'true' (default):
 * - Shows both "Every 24 hours" and "Every 4 hours" options
 * - Allows users to select either cadence option
 *
 * Environment Variable: REACT_APP_AI_INSIGHTS_ENABLED
 *
 * When set to 'false':
 * - Hides all AI Insights CTAs from overview sections
 * - Prevents users from accessing the AI Insights modal
 *
 * When set to 'true' (default):
 * - Shows AI Insights CTAs in relevant sections
 * - Allows users to open and interact with the AI Insights modal
 *
 * Environment Variable: REACT_APP_VISIBLE_ACTIVATION_TYPES
 *
 * Comma-separated allowlist of the spec-driven Activation destinations the ADD-integration catalog may offer, e.g.
 * `amplitude,hubspot,sendgrid,generic-webhook`. It is per-environment, so prod can start with a small wave while
 * staging runs everything — without a code change or a deploy.
 *
 * - unset or empty (the default): NO Activation card appears, so none can be created, and the legacy "coming soon"
 *   placeholder cards a spec would supersede stay visible. A new environment is therefore safe by default.
 * - `*`: every shipped spec is offered.
 * - a list: only those activation types are offered; unknown entries are simply ignored.
 *
 * The allowlist gates the ADD catalog ONLY. Already-created Activations always resolve their spec, so shrinking the
 * list never strands existing integrations behind a card that no longer exists.
 *
 * Environment Variable: REACT_APP_ACTIVATION_TEMPLATES_ENABLED
 *
 * Opt-in (default 'false'/unset): ships the VIQ-3756 activation-templates feature dark.
 *
 * When set to 'true':
 * - Shows the "Start from template" entry point on supported Available Activations rows
 * - Shows the "Save template" banner on the create/detail activation pages
 *
 * Environment Variable: REACT_APP_MATCHBACK_ACTIVATIONS_ENABLED
 *
 * Opt-in (default 'false'/unset): Matchback activations aren't backed by the API yet, so prod ships them dark.
 *
 * When set to 'true':
 * - Shows the Activations tab on the Matchback detail page (preview only)
 *
 * Environment Variable: REACT_APP_MATCHBACKS_FEATURE_ENABLED
 *
 * Opt-in (default 'false'/unset); set to 'true' in every stage (environment/commonEnvVars.ts).
 *
 * When set to 'true':
 * - Shows the Matchbacks tab on Customer Lists and the Matchback detail pages
 * - Shows the Matchbacks filter in the sidebar and the audiences list header
 * - Lets a Matchback be a Lookalike's base audience
 *
 * Environment Variable: REACT_APP_API_ACCESS_TOKENS_ENABLED
 *
 * Opt-in (default 'false'/unset): ships the self-service API access tokens page dark in production.
 *
 * When set to 'true':
 * - Adds the /app/settings/api-access-tokens page
 * - Shows its links in the user menu and, on mobile, on the Profile page
 */

import { useSessionContext } from '~/contexts/SessionContext';
import { PixelMatchingCadence } from '~/types/api.graphql';

/**
 * Checks if the 4-hour pixel cadence feature is enabled
 * @returns boolean - true if 4-hour cadence is enabled, false otherwise
 */
export const isPixel4HourCadenceEnabled = (): boolean => {
  return process.env.REACT_APP_PIXEL_4_HOUR_CADENCE_ENABLED === 'true';
};

/**
 * Gets the default pixel cadence based on the feature flag
 * @returns PixelMatchingCadence - TwentyFourHours if feature is disabled, otherwise the provided value
 */
export const getDefaultPixelCadence = (value?: PixelMatchingCadence): PixelMatchingCadence => {
  if (!isPixel4HourCadenceEnabled()) {
    return PixelMatchingCadence.TwentyFourHours;
  }
  return value || PixelMatchingCadence.TwentyFourHours;
};

const ALL_ACTIVATION_TYPES = '*';

/**
 * The activation types this environment may offer in the ADD catalog. An empty set means none (the safe default for
 * an environment that never sets the variable); a set holding '*' means every shipped spec.
 */
export const visibleActivationTypes = (): Set<string> =>
  new Set(
    (process.env.REACT_APP_VISIBLE_ACTIVATION_TYPES || '')
      .split(',')
      .map((activationType) => activationType.trim())
      .filter(Boolean),
  );

/** True when the ADD catalog may offer this specific destination. */
export const isActivationTypeVisible = (activationType: string, visibleTypes = visibleActivationTypes()): boolean =>
  visibleTypes.has(ALL_ACTIVATION_TYPES) || visibleTypes.has(activationType);

/**
 * Hook that checks if AI Insights is enabled for the current user.
 * If the global flag is 'true', everyone has access.
 * If 'false', checks the email whitelist.
 */
export const useAIInsightsEnabled = (): boolean => {
  const { me } = useSessionContext();

  if (process.env.REACT_APP_AI_INSIGHTS_ENABLED !== 'false') {
    return true;
  }

  const whitelist = process.env.REACT_APP_AI_INSIGHTS_WHITELIST || '';
  if (!whitelist || !me?.email) {
    return false;
  }

  const userEmail = me.email.toLowerCase();
  const entries = whitelist.split(',').map((e) => e.trim().toLowerCase());
  // To allow specific emails or some by domain like @trupropel.com @visitiq.io
  return entries.some((entry) => (entry.startsWith('@') ? userEmail.endsWith(entry) : userEmail === entry));
};

/**
 * Checks if the activation-templates feature (VIQ-3756) is enabled.
 * @returns boolean - true only when explicitly enabled; ships dark otherwise
 */
export const isActivationTemplatesEnabled = (): boolean => {
  return process.env.REACT_APP_ACTIVATION_TEMPLATES_ENABLED === 'true';
};

/**
 * Checks if the Activations tab on the Matchback detail page is enabled.
 * @returns boolean - true only when explicitly enabled; ships dark otherwise
 */
export const isMatchbackActivationsEnabled = (): boolean => {
  return process.env.REACT_APP_MATCHBACK_ACTIVATIONS_ENABLED === 'true';
};

/**
 * Checks if the Matchbacks feature is enabled.
 * @returns boolean - true only when explicitly enabled
 */
export const isMatchbacksEnabled = (): boolean => {
  return process.env.REACT_APP_MATCHBACKS_FEATURE_ENABLED === 'true';
};

/**
 * Checks if the self-service API access tokens page is enabled.
 * @returns boolean - true only when explicitly enabled; ships dark otherwise
 */
export const isApiAccessTokensEnabled = (): boolean => {
  return process.env.REACT_APP_API_ACCESS_TOKENS_ENABLED === 'true';
};
